import React, { useState, useMemo } from 'react';
import DataTable, { DataTableColumn } from 'components/common/DataTable';

interface TabTableWithSearchProps<T> {
  readonly data: T[];
  readonly columns: DataTableColumn<T>[];
  readonly placeholder?: string;
  readonly onRowClick?: (row: T, tabType?: string) => void;
  /** When provided, table name links use this URL so "open in new tab" works correctly. */
  readonly getRowTo?: (row: T, tabType?: string) => string;
  readonly tabType?: string;
  readonly searchValue?: string;
  readonly onSearchChange?: (value: string) => void;
  readonly customFilters?: React.ReactNode;
  readonly showArrowColumn?: boolean;
  readonly customRenderers?: {
    [key: string]: (value: any, row: T) => React.ReactNode;
  };
  readonly isSensorData?: boolean;
  readonly timeRange?: any;
  readonly onTimeRangeChange?: (timeRange: any) => void;
  readonly refreshIcon?: string;
  readonly emptyMessage?: string;
}

function TabTableWithSearch<T extends object>({ 
  data, 
  columns, 
  placeholder = 'Search...', 
  onRowClick, 
  getRowTo,
  tabType,
  searchValue = '',
  onSearchChange,
  customFilters,
  showArrowColumn = false,
  customRenderers = {},
  isSensorData: _isSensorData = false,
  timeRange: _timeRange,
  onTimeRangeChange: _onTimeRangeChange,
  refreshIcon: _refreshIcon,
  emptyMessage = 'No data'
}: TabTableWithSearchProps<T>) {

  const [internalSearch, setInternalSearch] = useState(searchValue);

  // Use controlled search if provided, otherwise use internal state
  const currentSearch = onSearchChange ? searchValue : internalSearch;
  const handleSearchChange = onSearchChange ?? setInternalSearch;

  // Filter data by search term - only by "name" columns
  const filteredData = useMemo(() => {
    if (!currentSearch) return data;

    const searchLower = currentSearch.toLowerCase();
    // Consider only columns whose key contains "name" for search
    const nameKeys = columns
      .map(col => col.key)
      .filter(key => key.toLowerCase().includes('name'));

    // If no name-like columns, fall back to returning all data
    if (nameKeys.length === 0) {
      return data;
    }

    return data.filter(row =>
      nameKeys.some(key => {
        const value = (row as any)[key];
        return (
          value !== undefined &&
          value !== null &&
          String(value).toLowerCase().includes(searchLower)
        );
      })
    );
  }, [data, columns, currentSearch]);

  // Process columns with custom renderers
  const processedColumns = useMemo(() => {
    let processed = [...columns] as DataTableColumn<T>[];

    // Apply custom renderers to columns
    processed = processed.map(column => {
      const customRenderer = customRenderers[column.key];
      if (customRenderer) {
        return {
          ...column,
          render: (row: any) => customRenderer(row[column.key], row)
        };
      }
      return column;
    });

    // Add arrow column if requested
    if (showArrowColumn && onRowClick) {
      processed.push({
        label: '',
        key: '__arrow__',
        render: (row: any) => (
          <div className="gatewayList-row-detail-action">
            <button
              onClick={() => onRowClick(row, tabType ?? '')}
              style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                color: 'inherit' 
              }}
              aria-label={`Navigate to ${row.name ?? 'item'} details`}
            >
              <i className="material-icons right">keyboard_arrow_right</i>
            </button>
          </div>
        ),
        sortable: false,
        colProps: { 
          style: { textAlign: 'right', width: 32 } 
        },
      });
    }

    return processed;
  }, [columns, customRenderers, showArrowColumn, onRowClick, tabType]);

  return (
    <>
      {/* Search and Filters Section */}
      <div 
        className="ldsbus-search" 
        style={{ 
          margin: '16px 0', 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center' 
        }}
      >
        {/* Custom Filters */}
        {customFilters && (
          <div className="mr-2 order-1 order-sm-0">
            {customFilters}
          </div>
        )}
        
        {/* Search Input */}
        <input
          type="text"
          className="form-control"
          style={{ 
            maxWidth: 240, 
            background: '#232b3e', 
            color: '#fff', 
            border: '1px solid #2f3a51', 
            borderRadius: 6 
          }}
          placeholder={placeholder}
          value={currentSearch}
          onChange={e => handleSearchChange(e.target.value)}
          aria-label="Search table data"
        />
      </div>

      {/* Data Table */}
      <DataTable 
        columns={processedColumns} 
        data={filteredData} 
        emptyContent={<div>{emptyMessage}</div>}
        onRowClick={onRowClick}
        getRowTo={getRowTo}
        tabType={tabType}
      />
    </>
  );
}

export default TabTableWithSearch;

 